import type { InputValveOptions } from '@/types/hydraulic'

/**
 * SCRUM-1032 — which output decoders a valve row may be wired to.
 *
 * The picker used to offer every output decoder on the controller — all 990 of
 * them on a large site, mostly irrigation decoders already driving stations.
 * Two of those are wrong for different reasons:
 *
 * - a decoder already used by a station cannot also drive a valve, so offering
 *   it invites a mistake the user finds out about later;
 * - a valve's type says what it is wired to, and the rest are not candidates.
 *
 * Matching is by purpose *name*, resolved from the controller's own decoder
 * type list. `OutputDecoderType` in the codebase does not describe the API any
 * more: it claims `MV_NORMALLY_OPEN = 5` and `MV_NORMALLY_CLOSED = 6`, where
 * the API answers with a single "Master Valve" at 5 and has no 6 at all.
 * Matching on those ids silently offered nothing for a normally closed valve.
 */

/** The decoder purpose each valve type has to be wired to, by name. */
export const PURPOSE_NAME_BY_VALVE_TYPE: Record<number, string> = {
  // Pump
  0: 'Pump Start',
  // Normally open and normally closed are the same wiring: a master valve.
  1: 'Master Valve',
  2: 'Master Valve',
  // Booster pump
  3: 'Booster Pump',
}

/**
 * Ids as the controller answered them at the time of writing, used only when
 * the decoder type list has not loaded yet. Kept out of `OutputDecoderType`
 * deliberately — that enum is the thing that was wrong.
 */
const FALLBACK_PURPOSE_ID_BY_NAME: Record<string, number> = {
  pumpstart: 4,
  mastervalve: 5,
  boosterpump: 7,
}

const normalise = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

/** `address-index` — how a decoder is identified across every endpoint here. */
export const decoderKeyOf = (decoder: {
  address?: number | string
  index?: number | string
}) => `${decoder?.address}-${decoder?.index}`

/** Purpose name → id, from the controller's own decoder type list. */
export const buildPurposeIdByName = (
  decoderTypes: { id: number | string; name?: string }[] | undefined,
): Map<string, number> => {
  const map = new Map<string, number>()
  ;(decoderTypes ?? []).forEach((type) => {
    if (type?.name) map.set(normalise(type.name), Number(type.id))
  })
  return map
}

interface AvailableValveDecodersArgs {
  options: InputValveOptions[]
  /** The row's valve type: pump, booster pump, or a master valve either way. */
  valveType?: number | null
  /** `address-index` of every decoder a station already drives. */
  stationDecoderKeys: Set<string>
  /** Purpose name → id for this controller; falls back when it has not loaded. */
  purposeIdByName?: Map<string, number>
  /**
   * The decoder this row already holds. Kept whatever the rules say: a saved
   * valve must not lose its own decoder from its own picker because the row
   * was set up before these rules existed, or because the decoder's purpose
   * has been changed since.
   */
  currentKey?: string
}

export const availableValveDecoders = ({
  options,
  valveType,
  stationDecoderKeys,
  purposeIdByName,
  currentKey,
}: AvailableValveDecodersArgs): InputValveOptions[] => {
  const purposeName = PURPOSE_NAME_BY_VALVE_TYPE[Number(valveType)]
  const key = purposeName ? normalise(purposeName) : undefined
  const purposeId = key
    ? (purposeIdByName?.get(key) ?? FALLBACK_PURPOSE_ID_BY_NAME[key])
    : undefined

  return options.filter((option) => {
    const optionKey = decoderKeyOf(option)
    if (currentKey && optionKey === currentKey) return true
    if (stationDecoderKeys.has(optionKey)) return false
    // An unknown valve type narrows nothing rather than emptying the list:
    // better to offer too much than to offer nothing at all.
    if (purposeId === undefined) return true
    return Number((option as any).decoderTypeId) === purposeId
  })
}
